import { Controller } from "@hotwired/stimulus"
import { filterAndSortRoles, escapeHtml } from "../utils/role_search"
import { bindClickOutside } from "../utils/click_outside"
import { handleListKeydown } from "../utils/keyboard_nav"

export default class extends Controller {
  static targets = ["input", "results", "hiddenField", "variantsData", "warning"]

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.filteredRoles = []
    this.roles = []

    if (this.hasVariantsDataTarget) {
      try {
        this.roles = JSON.parse(this.variantsDataTarget.textContent)
      } catch (e) {
        console.error("Failed to parse roles JSON:", e)
      }
    }

    this.unbindClickOutside = bindClickOutside(this.element, () => this.closeResults())
  }

  disconnect() {
    this.unbindClickOutside()
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.closeResults()
      return
    }

    this.filteredRoles = filterAndSortRoles(this.roles, query)
    this.renderResults()
    this.openResults()
  }

  renderResults() {
    if (this.filteredRoles.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-gray-500 text-sm">
          No matching roles found
        </div>
      `
      return
    }

    const html = this.filteredRoles.map((role, index) => {
      const isHighlighted = index === this.highlightedIndex
      const hasData = role.has_salary_data
      const textClass = hasData ? 'text-gray-900' : 'text-gray-400'
      const badge = hasData ? '' : '<span class="text-xs text-amber-600 ml-1">No data</span>'
      return `
        <div class="px-4 py-2 cursor-pointer ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-gray-50'}"
             data-action="click->admin-job-role-autocomplete#select"
             data-index="${index}">
          <div class="font-medium text-sm ${textClass}">${escapeHtml(role.canonical_title)}${badge}</div>
          <div class="text-xs text-gray-500">${escapeHtml(role.career_family || '')}</div>
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
  }

  openResults() {
    this.resultsTarget.classList.remove("hidden")
    this.isOpen = true
    this.highlightedIndex = -1
  }

  closeResults() {
    this.resultsTarget.classList.add("hidden")
    this.isOpen = false
    this.highlightedIndex = -1
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const role = this.filteredRoles[index]
    if (role) this.selectRole(role)
  }

  selectRole(role) {
    this.inputTarget.value = role.canonical_title
    this.hiddenFieldTarget.value = role.id

    if (this.hasWarningTarget) {
      if (!role.has_salary_data) {
        this.warningTarget.textContent = "This role may not have enough salary data to generate a video."
        this.warningTarget.classList.remove("hidden")
      } else {
        this.warningTarget.textContent = ""
        this.warningTarget.classList.add("hidden")
      }
    }

    this.closeResults()

    this.element.dispatchEvent(new CustomEvent("role:selected", {
      bubbles: true,
      detail: {
        roleId: role.id,
        canonicalTitle: role.canonical_title,
        hasSalaryData: role.has_salary_data
      }
    }))
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenFieldTarget.value = ""
    this.closeResults()

    if (this.hasWarningTarget) {
      this.warningTarget.textContent = ""
      this.warningTarget.classList.add("hidden")
    }

    this.element.dispatchEvent(new CustomEvent("role:cleared", { bubbles: true }))
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    handleListKeydown(event, {
      highlightedIndex: this.highlightedIndex,
      itemCount: this.filteredRoles.length,
      onHighlight: (index) => { this.highlightedIndex = index; this.renderResults() },
      onSelect: (index) => this.selectRole(this.filteredRoles[index]),
      onClose: () => this.closeResults()
    })
  }
}
